import { useState, type CSSProperties } from "react";
import { Pill, EmptyState, HostChip, showToast } from "../primitives";
import { Panel, PanelRow } from "./Panel";
import {
  useActivitiesByActor,
  useArchiveActivity,
  useRestoreActivity,
  useDeleteActivity,
} from "../../hooks/use-activities";
import { useIsOwner } from "../../hooks/use-is-owner";
import { PanelRowMenu } from "./PanelRowMenu";
import { ConfirmDeleteActivityModal } from "./ConfirmDeleteActivityModal";
import { ArchivedBadge, ShowArchivedChip, useShowArchived } from "./panel-archive";
import { formatRelativeTime } from "../../lib/v3-columns";
import { CHANNEL_ICON, CHANNEL_LABEL, DIRECTION_ICON, primaryTarget, targetLabel } from "../../lib/activity-display";
import type { Activity } from "../../../lib/types";

interface ActivityPanelProps {
  /** The agent node — Activities where this agent is the actor are shown. */
  nodeId: string;
  actionsBelowTitle?: boolean;
}

/**
 * Sprint 14 — product-facing interaction history (HISTORY) where this agent is
 * the actor. Distinct from RecentActivity (the system-facing workspace audit
 * log). Rows are recorded by hosts via create_activity.
 *
 * Sprint 14g — standardised on the unified archive/delete UX: a "Show archived"
 * chip, a per-row ⋮ menu (Archive / Restore / owner-only Delete), an undo toast
 * on archive, and a reason-required delete modal (Activity is an audit record).
 */
export function ActivityPanel({ nodeId, actionsBelowTitle }: ActivityPanelProps) {
  const [showArchived, setShowArchived] = useShowArchived("activity", nodeId);
  const query = useActivitiesByActor("agent", nodeId, 20, showArchived);
  const activities = query.data ?? [];

  return (
    <Panel
      title="Activity"
      count={activities.length}
      action={<ShowArchivedChip value={showArchived} onChange={setShowArchived} />}
      actionsBelowTitle={actionsBelowTitle}
      isLoading={query.isLoading}
      error={query.error}
      onRetry={() => query.refetch()}
      isEmpty={activities.length === 0}
      empty={
        showArchived ? (
          <EmptyState>No archived items. Items you archive will appear here for restore.</EmptyState>
        ) : (
          <EmptyState>
            No activity recorded for this agent yet. Hosts log interactions (emails, calls, meetings)
            via Claude / Cowork as they happen.
          </EmptyState>
        )
      }
    >
      {activities.map((a) => (
        <ActivityRow key={a.id} activity={a} deletable />
      ))}
    </Panel>
  );
}

export function ActivityRow({
  activity,
  onClick,
  deletable,
}: {
  activity: Activity;
  onClick?: () => void;
  /** Sprint 14g — when true, shows the unified "⋮" menu (Archive / Restore /
   *  owner-only Delete). Used by the cockpit ActivityPanel and the row-detail
   *  timeline where a user manages records. */
  deletable?: boolean;
}) {
  const target = primaryTarget(activity);
  const extra = activity.regarding.length - 1;
  const isArchived = !!activity.archived_at;
  return (
    <PanelRow
      onClick={onClick}
      title={activity.subject}
      trailing={deletable ? <ActivityRowActions activity={activity} clickable={!!onClick} /> : undefined}
    >
      <i
        className={`ti ${CHANNEL_ICON[activity.channel]}`}
        style={{ fontSize: 16, color: "var(--color-text-tertiary)", flexShrink: 0, alignSelf: "flex-start", marginTop: 2 }}
        title={CHANNEL_LABEL[activity.channel]}
        aria-hidden="true"
      />
      <div style={{ flex: 1, minWidth: 0, opacity: isArchived ? 0.6 : 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <i className={`ti ${DIRECTION_ICON[activity.direction]}`} style={{ fontSize: 12, color: "var(--color-text-tertiary)", flexShrink: 0 }} aria-hidden="true" />
          <span style={subjectStyle}>{activity.subject}</span>
          {isArchived && <ArchivedBadge />}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
          {target && <Pill variant="gray">{targetLabel(target)}</Pill>}
          {extra > 0 && <span style={metaStyle}>+{extra} more</span>}
          {activity.summary && <span style={summaryStyle}>{activity.summary}</span>}
        </div>
      </div>
      {activity.created_via && <HostChip kind={activity.created_via} />}
      <span style={timeStyle}>{formatRelativeTime(activity.occurred_at)}</span>
    </PanelRow>
  );
}

/**
 * Sprint 14g — the unified "⋮" row menu for a single Activity. Archive is a
 * recoverable soft-delete (with an undo toast); Restore brings it back; Delete
 * is owner-only and routes through ConfirmDeleteActivityModal, which requires a
 * free-text reason (recorded into audit_log). Activity is a leaf — no back-ref
 * surface, no force option.
 */
function ActivityRowActions({ activity, clickable }: { activity: Activity; clickable?: boolean }) {
  const archiveActivity = useArchiveActivity();
  const restoreActivity = useRestoreActivity();
  const deleteActivity = useDeleteActivity();
  const canDelete = useIsOwner();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isArchived = !!activity.archived_at;

  function handleArchive() {
    archiveActivity.mutate(activity.id, {
      onSuccess: () =>
        showToast(`Archived '${activity.subject}'`, "success", false, {
          label: "Undo",
          onClick: () => restoreActivity.mutate(activity.id),
        }, 10000),
      onError: (e) => showToast(e instanceof Error ? e.message : "Archive failed", "error", true),
    });
  }

  function handleRestore() {
    restoreActivity.mutate(activity.id, {
      onSuccess: () => showToast(`Restored '${activity.subject}'`),
      onError: (e) => showToast(e instanceof Error ? e.message : "Restore failed", "error", true),
    });
  }

  const menu = (
    <PanelRowMenu
      isArchived={isArchived}
      canDelete={canDelete}
      onArchive={handleArchive}
      onRestore={handleRestore}
      onDelete={() => setConfirmDelete(true)}
    />
  );

  return (
    <>
      {clickable ? (
        <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex", alignSelf: "flex-start" }}>
          {menu}
        </span>
      ) : (
        menu
      )}

      <ConfirmDeleteActivityModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        subject={activity.subject}
        activityId={activity.id}
        onConfirm={async ({ reason }) => {
          await deleteActivity.mutateAsync({ id: activity.id, reason });
          showToast(`Deleted '${activity.subject}'`);
        }}
      />
    </>
  );
}

const subjectStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const summaryStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const metaStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
};

const timeStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
  flexShrink: 0,
  alignSelf: "flex-start",
  marginTop: 2,
};
